import * as React from 'react';
import { styled } from '@mui/material/styles';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { Divider, Button, Typography } from '@mui/material';

const drawerWidth = 240;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
});

// App.js の RoleGuard と整合させる: allowedRoles 未指定なら全ロール表示
const menuItems = [
  { text: 'ダッシュボード', icon: <DashboardOutlinedIcon />, path: '/' },
  { text: '受注管理', icon: <ReceiptLongIcon />, path: '/orders', allowedRoles: ['admin', 'manager', 'developer'] },
  { text: '請求書管理', icon: <RequestQuoteOutlinedIcon />, path: '/invoices', allowedRoles: ['admin', 'manager', 'developer'] },
  { text: '案件管理', icon: <WorkOutlineIcon />, path: '/projects' },
  { text: 'クライアント管理', icon: <PeopleOutlineIcon />, path: '/customers', allowedRoles: ['admin', 'manager', 'developer'] },
  { text: '外注先支払管理', icon: <PaymentsOutlinedIcon />, path: '/payments', allowedRoles: ['admin', 'manager', 'developer'] },
  { text: '契約管理', icon: <AssignmentOutlinedIcon />, path: '/contracts', allowedRoles: ['admin', 'manager', 'developer'] },
  { text: '外注先管理', icon: <PeopleOutlineIcon />, path: '/outsource', allowedRoles: ['admin', 'manager', 'developer'] },
  { text: 'ファイル管理', icon: <FolderOutlinedIcon />, path: '/files' },
  { text: 'ユーザー管理', icon: <PersonOutlineIcon />, path: '/users' },
  { text: 'システム設定', icon: <SettingsOutlinedIcon />, path: '/settings', allowedRoles: ['admin'] },
];

export default function SideMenu() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const filteredMenu = menuItems.filter(item =>
    !item.allowedRoles || item.allowedRoles.includes(user.role)
  );

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ mt: 'calc(var(--template-frame-height, 0px) + 4px)', px: 2, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          ログイン中
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {user.name || '不明'}
        </Typography>
      </Box>
      <Divider />
      {/* Windster 風: active item に左バー + bg-blue-50 + text-blue-700 */}
      <List sx={{ px: 1, py: 1 }}>
        {filteredMenu.map(({ text, icon, path }) => {
          const active = isActive(path);
          return (
            <ListItemButton
              key={text}
              onClick={() => navigate(path)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                position: 'relative',
                bgcolor: active ? 'primary.50' : 'transparent',
                color: active ? 'primary.700' : 'text.primary',
                fontWeight: active ? 600 : 400,
                '&:hover': {
                  bgcolor: active ? 'primary.100' : 'action.hover',
                },
                '&::before': active ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 8,
                  bottom: 8,
                  width: 3,
                  bgcolor: 'primary.main',
                  borderRadius: '0 2px 2px 0',
                } : {},
                '& .MuiListItemIcon-root': {
                  minWidth: 36,
                  color: active ? 'primary.main' : 'text.secondary',
                },
              }}
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText
                primary={text}
                primaryTypographyProps={{
                  fontWeight: active ? 600 : 500,
                  fontSize: '0.875rem',
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Divider sx={{ mt: 'auto' }} />
      <Box sx={{ p: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
        >
          ログアウト
        </Button>
      </Box>
    </Drawer>
  );
}
